import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { InnerPageLayout } from "./InnerPageLayout";
import { ABOUT } from "../data/pages";
import { Seo } from "../components/site/Seo";

export const AboutPage = () => {
  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <InnerPageLayout title="About Techovarya">
      <Seo
        title="About"
        description={ABOUT.intro}
        path="/about"
      />
      {/* Hero */}
      <section
        id="top"
        data-testid="about-hero"
        className="relative overflow-hidden bg-[#050505] pt-16 md:pt-24 pb-16 md:pb-20"
      >
        <div className="absolute inset-0 bg-grid opacity-40" aria-hidden="true" />
        <div className="absolute inset-0 radial-spotlight" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-8">
            <Link to="/" className="hover:text-[#FF5F1F] transition-colors">
              Home
            </Link>
            <span className="text-zinc-700">/</span>
            <span>About</span>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#FF5F1F]" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
              {ABOUT.eyebrow}
            </span>
          </div>

          <h1
            data-testid="about-h1"
            className="font-headings text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[1.02] text-white max-w-5xl"
          >
            {ABOUT.h1}
          </h1>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <p className="lg:col-span-7 text-base md:text-lg text-zinc-300 leading-relaxed">
              {ABOUT.intro}
            </p>
            <p className="lg:col-span-5 text-base md:text-lg text-zinc-400 leading-relaxed lg:pt-2 lg:border-l lg:border-white/10 lg:pl-8">
              {ABOUT.subIntro}
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section
        data-testid="about-story"
        className="relative bg-[#0A0A0A] border-t border-white/10 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#FF5F1F]" />
              <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                {ABOUT.story.eyebrow}
              </span>
            </div>
            <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] text-white">
              {ABOUT.story.heading}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            {ABOUT.story.body.map((p, i) => (
              <p key={i} className="text-base md:text-lg text-zinc-300 leading-relaxed">
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section
        data-testid="about-philosophy"
        className="relative bg-[#050505] border-t border-white/10 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-14 md:mb-20 max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#FF5F1F]" />
              <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                {ABOUT.philosophy.eyebrow}
              </span>
            </div>
            <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] text-white">
              {ABOUT.philosophy.heading}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {ABOUT.philosophy.items.map(([label, desc], i) => (
              <motion.div
                key={i}
                data-testid={`philosophy-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                className="bg-[#0A0A0A] hover:bg-[#111] transition-colors p-6 md:p-10 flex gap-6"
              >
                <div className="font-headings text-4xl md:text-5xl font-black text-zinc-800 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1">
                  <h3 className="font-headings text-xl md:text-2xl font-bold text-white tracking-tight mb-3">
                    {label}
                  </h3>
                  <p className="text-sm md:text-base text-zinc-400 leading-relaxed">{desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section
        data-testid="about-leadership"
        className="relative bg-[#0A0A0A] border-t border-white/10 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-14 md:mb-20 max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#FF5F1F]" />
              <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                {ABOUT.leadership.eyebrow}
              </span>
            </div>
            <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] text-white">
              {ABOUT.leadership.heading}
            </h2>
          </div>

          <div className="border border-white/10 bg-[#050505] p-6 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
              {/* Portrait placeholder */}
              <div className="lg:col-span-4">
                <div className="aspect-square border border-white/10 bg-gradient-to-br from-[#0A0A0A] to-[#151515] flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-grid opacity-30" aria-hidden="true" />
                  <div className="relative font-headings text-[140px] md:text-[180px] font-black text-[#FF5F1F]/80 leading-none">
                    HJ
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <span className="w-1.5 h-1.5 bg-[#FF5F1F] animate-accent-pulse" />
                  Founder Portrait
                </div>
              </div>

              <div className="lg:col-span-8 flex flex-col gap-6">
                <div>
                  <h3 className="font-headings text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight">
                    {ABOUT.leadership.name}
                  </h3>
                  <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#FF5F1F]">
                    {ABOUT.leadership.title}
                  </p>
                </div>
                <p className="text-base md:text-lg text-zinc-300 leading-relaxed">
                  {ABOUT.leadership.bio}
                </p>
                <div className="pt-6 border-t border-white/10">
                  <a
                    href="#"
                    data-testid="leadership-cta"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToContact();
                    }}
                    className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-white hover:text-[#FF5F1F] transition-colors"
                  >
                    {ABOUT.leadership.cta}
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global reach */}
      <section
        data-testid="about-reach"
        className="relative bg-[#050505] border-t border-white/10 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#FF5F1F]" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
              Global Reach
            </span>
          </div>
          <h2 className="font-headings text-3xl md:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05] max-w-3xl">
            Building software for teams across 5 continents.
          </h2>

          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {["USA", "United Kingdom", "Europe", "UAE", "Australia"].map((m, i) => (
              <div key={i} className="bg-[#0A0A0A] p-8 flex flex-col gap-2 items-center text-center">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F]">
                  /{String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-headings text-lg md:text-2xl font-bold text-white tracking-tight">
                  {m}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </InnerPageLayout>
  );
};
